import { Checkbox, Group, Stack, Tooltip } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { IconFile } from "@tabler/icons-react"
import {
  PaginationState,
  RowSelectionState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Button, ActionIcon } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"

import { ProductResponses } from "@tfg/types/endpoints/products"
import { fetchDataOptionsValidationType } from "@tfg/types/schemas/products"

import { Table } from "~/components/table/Table.tsx"
import { ComponentDetailsDrawer } from "~/pages/Brand/Regulations/RDUE/ComponentDetailsDrawer.tsx"
import { useRdueColumns } from "~/pages/Brand/Regulations/RDUE/columns.tsx"
import {
  useSyncProductsMutation,
  useValidateProductMutation,
} from "~/pages/Brand/Regulations/RDUE/hooks.ts"

interface Props {
  data: ProductResponses.GetRDUEProducts
  fetchDataOptions: fetchDataOptionsValidationType
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

export const ProductsRDUETAble = ({
  data,
  fetchDataOptions,
  setPagination,
}: Props) => {
  const { t } = useTranslation("regulations")

  const [selectCompliant, setSelectCompliant] = useState<boolean>(false)

  const maxComponentCount = data.products.reduce((acc, product) => {
    return Math.max(acc, product.components.length)
  }, 0)

  const [opened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)

  const [component, setComponent] = useState<ProductResponses.RDUEComponent>()

  const viewComponentDetails = (component: ProductResponses.RDUEComponent) => {
    openDrawer()
    setComponent(component)
  }

  const columns = useRdueColumns(maxComponentCount, viewComponentDetails)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const table = useReactTable({
    state: {
      pagination: fetchDataOptions,
      rowSelection,
    },
    manualPagination: true,
    data: data.products,
    pageCount: Math.ceil(data.totalProducts / fetchDataOptions.pageSize),
    columns,

    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
  })

  const selectedProducts = {
    ids: table.getSelectedRowModel().rows.map((row) => row.original.id),
  }

  const { mutate: validateProducts, isLoading: isValidating } =
    useValidateProductMutation()
  const { mutate: syncProducts, isLoading: isSyncing } =
    useSyncProductsMutation({
      onSuccess: (count: number) => {
        notifications.show({
          message: `${count} products updated`,
          color: "blue",
        })
      },
      onError: () => {
        notifications.show({
          message: `Error syncing products`,
          color: "red",
        })
      },
    })

  return (
    <Stack>
      <Group>
        <Checkbox
          label={<Stack>{t("lists.labels.selectCompliant")}</Stack>}
          checked={selectCompliant}
          onChange={(event) => {
            setSelectCompliant(event.currentTarget.checked)
            table.getRowModel().rows.forEach((row) => {
              if (event.currentTarget.checked) {
                row.toggleSelected(row.original.rdueCompliance === "READY")
              } else {
                row.toggleSelected(false)
              }
            })
          }}
        />
        <Button
          onClick={() => {
            validateProducts(selectedProducts)
          }}
          loading={isValidating}
          toolTip={t("lists.labels.dueDiligence", {
            count: selectedProducts.ids.length,
          })}
          icon={
            <Tooltip
              label={t("lists.labels.dueDiligence", {
                count: selectedProducts.ids.length,
              })}
            >
              <IconFile />
            </Tooltip>
          }
        >
          {t("lists.labels.dueDiligence")}
        </Button>
        <ActionIcon
          type="refresh"
          toolTip="Sync products"
          size={24}
          onClick={syncProducts}
          loading={isSyncing}
        />
      </Group>
      {component && (
        <ComponentDetailsDrawer
          component={component}
          onClose={closeDrawer}
          opened={opened}
        />
      )}

      <Table
        table={table}
        pagination={true}
        manualPaginationCount={data.totalProducts}
        pageIndex={fetchDataOptions.pageIndex}
      />
    </Stack>
  )
}
